import { POItemShipmentGroup, POItemProductGroup, POItemPurchaseOrderGroup, POItemSupplierGroup, PurchaseOrderItem, POItemGroups } from "@domain/purchasing";
import { Shipment } from "@domain/shipment";
import { Supplier } from "@domain/suppliers";
import { GroupingOption } from "../types/types";

function supplierForGroup(item: PurchaseOrderItem): Supplier | null {
    const po = item.purchaseOrder;
    if (!po) return null;
    if (po.supplier) return po.supplier;
    if (po.supplierId) {
        return {
            id: po.supplierId,
            name: "Unknown supplier",
            createdAt: new Date(0),
            updatedAt: new Date(0),
            isActive: true,
            addresses: [],
            supplierProducts: [],
        };
    }
    return null;
}

export const groupBySupplier = (items: PurchaseOrderItem[]): POItemSupplierGroup[] => {
    const map = new Map<string, { supplier: Supplier; items: PurchaseOrderItem[] }>();

    for (const item of items) {
        const supplier = supplierForGroup(item);
        if (!supplier) continue;

        const existing = map.get(supplier.id);
        if (existing) {
            existing.items.push(item);
        } else {
            map.set(supplier.id, { supplier, items: [item] });
        }
    }

    return Array.from(map.values()).map(({ supplier, items: groupItems }) =>
        new POItemSupplierGroup(
            supplier.id,
            supplier.name,
            groupItems.sort((a, b) => (a.createdAt || new Date()).getTime() - (b.createdAt || new Date()).getTime()),
            supplier
        )
    );
};

export const groupBy = (items: PurchaseOrderItem[], groupingOption: GroupingOption): POItemGroups[] => {
    switch (groupingOption) {
        case GroupingOption.SHIPMENT:
            return groupByShipment(items);
        case GroupingOption.PRODUCT:
            return groupByProduct(items);
        case GroupingOption.PURCHASE_ORDER:
            return groupByPurchaseOrder(items);
        case GroupingOption.SUPPLIER:
            return groupBySupplier(items);
        case GroupingOption.NO_GROUPING:
            return [];
    }
    return [];
}

export const groupByShipment = (items: PurchaseOrderItem[]): POItemShipmentGroup[] => {
    const uniqueShipmentIds = new Set<string>();
    const uniqueShipments: Shipment[] = [];

    for (const item of items) {
        if (item.shipment && item.shipment.id && !uniqueShipmentIds.has(item.shipment.id)) {
            uniqueShipmentIds.add(item.shipment.id);
            uniqueShipments.push(item.shipment);
        }
    }

    return uniqueShipments.map(
        shipment =>
            new POItemShipmentGroup(
                shipment.id!,
                shipment.name!,
                items.filter(item => item.shipment?.id === shipment.id).sort((a, b) => (a.createdAt || new Date()).getTime() - (b.createdAt || new Date()).getTime()),
                shipment
            )
    );
}

export const groupByProduct = (items: PurchaseOrderItem[]): POItemProductGroup[] => {
    const uniqueProductIds = new Set<string>();
    const uniqueProducts: { [key: string]: any } = {};

    for (const item of items) {
        if (item.product && item.product.id && !uniqueProductIds.has(item.product.id)) {
            uniqueProductIds.add(item.product.id);
            uniqueProducts[item.product.id] = item.product;
        }
    }

    return Object.values(uniqueProducts).map(
        product =>
            new POItemProductGroup(
                product.id,
                product.title,
                items.filter(item => item.product?.id === product.id).sort((a, b) => (a.createdAt || new Date()).getTime() - (b.createdAt || new Date()).getTime()),
                product
            )
    );
}

export const groupByPurchaseOrder = (items: PurchaseOrderItem[]): POItemPurchaseOrderGroup[] => {
    const uniquePurchaseOrderIds = new Set<string>();
    const uniquePurchaseOrders: { [key: string]: any } = {};

    for (const item of items) {
        if (item.purchaseOrder && item.purchaseOrder.id && !uniquePurchaseOrderIds.has(item.purchaseOrder.id)) {
            uniquePurchaseOrderIds.add(item.purchaseOrder.id);
            uniquePurchaseOrders[item.purchaseOrder.id] = item.purchaseOrder;
        }
    }

    return Object.values(uniquePurchaseOrders).map(
        purchaseOrder =>
            new POItemPurchaseOrderGroup(
                purchaseOrder.id,
                purchaseOrder.poNumber || `No PO Number`,
                items.filter(item => item.purchaseOrder?.id === purchaseOrder.id).sort((a, b) => (a.createdAt || new Date()).getTime() - (b.createdAt || new Date()).getTime()),
                purchaseOrder
            )
    );
}

export const getUngroupedItems = (items: PurchaseOrderItem[], groupedBy: GroupingOption): PurchaseOrderItem[] => {
    if (groupedBy === GroupingOption.SHIPMENT) {
        return items.filter(item => !item.shipment?.id);
    }
    if (groupedBy === GroupingOption.PRODUCT) {
        return items.filter(item => !item.product?.id);
    }
    if (groupedBy === GroupingOption.PURCHASE_ORDER) {
        return items.filter(item => !item.purchaseOrder?.id);
    }
    if (groupedBy === GroupingOption.SUPPLIER) {
        return items.filter(item => !supplierForGroup(item));
    }
    return items;
}